import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { getAccount, getAccounts, getAuditLogs, getInvoices, getSubscriptions, getTenants, updateAccount } from '@/services'
import { AppButton, EmptyState, FilterChip, Modal, Skeleton, StatusBadge, TableSkeleton, Tabs, formatMoney, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { formatCurrency, formatDate, formatDateTime, relTime } from '@/lib/utils'

// La API guarda active/suspended/cancelled; el panel usa el vocabulario del prototipo.
function normalizeStatus(status: string): string {
  return ({ suspended: 'paused', cancelled: 'churned' } as Record<string, string>)[status] || status
}

function emptyToUndefined(value: string): string | undefined {
  return value.trim() || undefined
}

const DETAIL_TABS = [
  { id: 'general', label: 'General', icon: 'info' },
  { id: 'tenants', label: 'Tenants', icon: 'building' },
  { id: 'subscriptions', label: 'Suscripciones', icon: 'refresh' },
  { id: 'invoices', label: 'Facturas', icon: 'file' },
  { id: 'activity', label: 'Actividad', icon: 'activity' },
]

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Activo' },
  { value: 'paused', label: 'Pausado' },
  { value: 'churned', label: 'Churned' },
]

const EMPTY_ACCOUNT_FORM = {
  name: '',
  legal_name: '',
  email: '',
  billing_email: '',
  phone: '',
  tax_id: '',
  address: '',
  city: '',
  state: '',
  country_id: '',
  status: 'lead',
}

function AccountDetailSkeleton() {
  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <Skeleton width={80} height={14} style={{ marginBottom: 10 }} />
          <div className="row gap-3" style={{ alignItems: 'center' }}>
            <Skeleton width={240} height={32} />
            <Skeleton width={84} height={22} radius={999} />
          </div>
          <Skeleton width={340} height={14} style={{ marginTop: 10 }} />
        </div>
        <div className="page-actions">
          <Skeleton width={100} height={34} radius={8} />
        </div>
      </div>
      <Tabs active="general" onChange={() => undefined} tabs={DETAIL_TABS} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card"><div className="card-header"><h3>Información</h3></div><div className="card-body"><Skeleton height={190} /></div></div>
        <div className="card"><div className="card-header"><h3>Resumen comercial</h3></div><div className="card-body"><Skeleton height={190} /></div></div>
      </div>
    </>
  )
}

export function AccountsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const { data: result, isLoading } = useQuery({
    queryKey: ['accounts', search],
    queryFn: () => getAccounts({ search: search || undefined, pageSize: 100 }),
  })
  const { data: tenantsResult } = useQuery({
    queryKey: ['accounts-tenants'],
    queryFn: () => getTenants({ pageSize: 200 }),
  })

  const accounts = result?.data || []
  const tenants = tenantsResult?.data || []

  const statsByAccount = (id: string) => {
    const owned = tenants.filter((tenant) => tenant.account_id === id)
    return { count: owned.length, mrr: owned.reduce((sum, tenant) => sum + (tenant.plan_price || 0), 0) }
  }

  const statusCounts = accounts.reduce<Record<string, number>>((acc, account) => {
    const key = normalizeStatus(account.status)
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})
  const filtered = statusFilter === 'all' ? accounts : accounts.filter((account) => normalizeStatus(account.status) === statusFilter)
  const totalMRR = tenants.reduce((sum, tenant) => sum + (tenant.plan_price || 0), 0)

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Clientes</div>
          <h1>Accounts</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={260} height={14} /> : `${accounts.length} accounts · ${formatMoney(totalMRR)} MRR consolidado`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
          <AppButton variant="primary" icon="plus" onClick={() => toast.success('Modal "Nueva account" — pendiente de portar')}>Nueva account</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="table-search">
            <Icon name="search" size={14} />
            <input placeholder="Buscar por nombre o email…" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <FilterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="Todas" count={isLoading ? undefined : accounts.length} />
          <FilterChip active={statusFilter === 'active'} onClick={() => setStatusFilter('active')} label="Activas" count={isLoading ? undefined : statusCounts.active || 0} />
          <FilterChip active={statusFilter === 'trial'} onClick={() => setStatusFilter('trial')} label="Trial" count={isLoading ? undefined : statusCounts.trial || 0} />
          <FilterChip active={statusFilter === 'lead'} onClick={() => setStatusFilter('lead')} label="Leads" count={isLoading ? undefined : statusCounts.lead || 0} />
          <FilterChip active={statusFilter === 'paused'} onClick={() => setStatusFilter('paused')} label="Pausadas" count={isLoading ? undefined : statusCounts.paused || 0} />
          <FilterChip active={statusFilter === 'churned'} onClick={() => setStatusFilter('churned')} label="Churned" count={isLoading ? undefined : statusCounts.churned || 0} />
        </div>

        {isLoading ? (
          <TableSkeleton columns={5} rows={8} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="briefcase" title="No hay accounts con ese filtro" desc="Probá limpiar la búsqueda o cambiar el filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Account</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ textAlign: 'right', width: 80 }}>Tenants</th>
                <th style={{ textAlign: 'right', width: 120 }}>MRR</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((account) => {
                const stats = statsByAccount(account.id)
                return (
                  <tr key={account.id} onClick={() => navigate(`/accounts/${account.id}`)} {...rowActivation(() => navigate(`/accounts/${account.id}`))}>
                    <td>
                      <div className="cell-primary">{account.name}</div>
                      <div className="cell-secondary">{account.email}</div>
                    </td>
                    <td><StatusBadge status={normalizeStatus(account.status)} size="sm" /></td>
                    <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{stats.count}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      {stats.mrr > 0 ? <span className="tabular cell-primary">{formatMoney(stats.mrr)}</span> : <span className="muted">—</span>}
                    </td>
                    <td><Icon name="moreVertical" size={14} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState('general')
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_ACCOUNT_FORM)

  const { data: account, isLoading, error } = useQuery({
    queryKey: ['account', id],
    queryFn: () => getAccount(id!),
    enabled: !!id,
  })
  const { data: subscriptionsResult, isLoading: subscriptionsLoading } = useQuery({
    queryKey: ['subscriptions', 'account', id],
    queryFn: () => getSubscriptions({ accountId: id!, pageSize: 100 }),
    enabled: !!id && (tab === 'general' || tab === 'subscriptions'),
  })
  const { data: invoicesResult, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', 'account', id],
    queryFn: () => getInvoices({ accountId: id!, pageSize: 100 }),
    enabled: !!id && (tab === 'general' || tab === 'invoices'),
  })
  const { data: auditResult, isLoading: auditLoading } = useQuery({
    queryKey: ['audit-logs', 'account', id],
    queryFn: () => getAuditLogs({ accountId: id!, pageSize: 100 }),
    enabled: !!id && tab === 'activity',
  })

  useEffect(() => {
    if (!account) return
    setForm({
      name: account.name || '',
      legal_name: account.legal_name || '',
      email: account.email || '',
      billing_email: account.billing_email || '',
      phone: account.phone || '',
      tax_id: account.tax_id || '',
      address: account.address || '',
      city: account.city || '',
      state: account.state || '',
      country_id: account.country_id || '',
      status: normalizeStatus(account.status),
    })
  }, [account])

  const saveMutation = useMutation({
    mutationFn: () => updateAccount(id!, {
      name: form.name.trim(),
      legal_name: emptyToUndefined(form.legal_name),
      email: form.email.trim().toLowerCase(),
      billing_email: emptyToUndefined(form.billing_email.toLowerCase()),
      phone: emptyToUndefined(form.phone),
      tax_id: emptyToUndefined(form.tax_id),
      address: emptyToUndefined(form.address),
      city: emptyToUndefined(form.city),
      state: emptyToUndefined(form.state),
      country_id: emptyToUndefined(form.country_id),
      status: form.status as any,
    }),
    onSuccess: () => {
      toast.success('Cliente actualizado')
      setEditOpen(false)
      qc.invalidateQueries({ queryKey: ['account', id] })
      qc.invalidateQueries({ queryKey: ['accounts'] })
      qc.invalidateQueries({ queryKey: ['audit-logs', 'account', id] })
    },
    onError: (e: any) => toast.error(e?.message || 'No se pudo guardar'),
  })

  if (isLoading) return <AccountDetailSkeleton />
  if (error || !account) {
    return (
      <div className="empty-state">
        <div className="emoji"><Icon name="briefcase" size={24} /></div>
        <h3>No se encontró el cliente</h3>
        <Link to="/accounts"><AppButton variant="ghost">Volver a clientes</AppButton></Link>
      </div>
    )
  }

  const tenants = account.tenants || []
  const subscriptions = subscriptionsResult?.data || []
  const invoices = invoicesResult?.data || []
  const auditLogs = auditResult?.data || []
  const subscriptionSummary = account.subscriptions_summary || { active: 0, past_due: 0, cancelled: 0, total: 0 }
  const mrr = subscriptions.reduce((sum, subscription) => sum + (subscription.plan_price || 0), 0)
  const paidTotal = invoices.filter((invoice) => invoice.status === 'paid').reduce((sum, invoice) => sum + (invoice.total || 0), 0)
  const openTotal = invoices.filter((invoice) => invoice.status === 'open' || invoice.status === 'pending').reduce((sum, invoice) => sum + (invoice.total || 0), 0)

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6 }}>
            <Link to="/accounts" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>← Clientes</Link>
          </div>
          <div className="row gap-3" style={{ alignItems: 'center' }}>
            <h1>{account.name}</h1>
            <StatusBadge status={normalizeStatus(account.status)} size="sm" />
          </div>
          <div className="subtitle row gap-2" style={{ flexWrap: 'wrap' }}>
            <span>{account.email}</span>
            {account.tax_id ? <span>· {account.tax_id}</span> : null}
            <span>· {tenants.length} tenants</span>
          </div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="edit" onClick={() => setEditOpen(true)}>Editar</AppButton>
          <AppButton variant="secondary" icon="mail" onClick={() => { window.location.href = `mailto:${account.email}` }}>Enviar email</AppButton>
        </div>
      </div>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Editar cliente"
        subtitle="Datos comerciales de la account en Platform"
        size="lg"
        footer={(
          <>
            <AppButton variant="secondary" onClick={() => setEditOpen(false)}>Cancelar</AppButton>
            <AppButton variant="primary" icon="check" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Guardar cambios</AppButton>
          </>
        )}
      >
        <div className="col gap-4">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-row">
              <label htmlFor="account-name">Nombre</label>
              <input id="account-name" className="input" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-legal-name">Razón social</label>
              <input id="account-legal-name" className="input" value={form.legal_name} onChange={(e) => setForm((prev) => ({ ...prev, legal_name: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-email">Email</label>
              <input id="account-email" className="input" type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-billing-email">Email billing</label>
              <input id="account-billing-email" className="input" type="email" value={form.billing_email} onChange={(e) => setForm((prev) => ({ ...prev, billing_email: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-phone">Teléfono</label>
              <input id="account-phone" className="input" value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-tax-id">CUIT/NIF</label>
              <input id="account-tax-id" className="input" value={form.tax_id} onChange={(e) => setForm((prev) => ({ ...prev, tax_id: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-status">Estado</label>
              <select id="account-status" className="select" value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
                {ACCOUNT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="account-country">País</label>
              <input id="account-country" className="input" value={form.country_id} onChange={(e) => setForm((prev) => ({ ...prev, country_id: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <div className="form-row">
              <label htmlFor="account-address">Dirección</label>
              <input id="account-address" className="input" value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-city">Ciudad</label>
              <input id="account-city" className="input" value={form.city} onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))} />
            </div>
            <div className="form-row">
              <label htmlFor="account-state">Provincia</label>
              <input id="account-state" className="input" value={form.state} onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value }))} />
            </div>
          </div>
        </div>
      </Modal>

      <Tabs active={tab} onChange={setTab} tabs={DETAIL_TABS.map((item) => item.id === 'tenants' ? { ...item, count: tenants.length } : item)} />

      {tab === 'general' ? (
        <div className="col gap-4">
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi-card"><div className="kpi-label"><Icon name="building" size={14} />Tenants</div><div className="kpi-value">{tenants.length}</div></div>
            <div className="kpi-card"><div className="kpi-label"><Icon name="refresh" size={14} />Suscripciones</div><div className="kpi-value">{subscriptionSummary.total}</div></div>
            <div className="kpi-card"><div className="kpi-label"><Icon name="trending" size={14} />MRR</div><div className="kpi-value">{formatCurrency(mrr, 'ARS')}</div></div>
            <div className="kpi-card"><div className="kpi-label"><Icon name="file" size={14} />Pendiente</div><div className="kpi-value">{formatCurrency(openTotal, 'ARS')}</div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              <div className="card-header"><h3>Información</h3></div>
              <div className="card-body">
                <dl className="dl" style={{ gridTemplateColumns: '130px 1fr' }}>
                  <dt>Nombre</dt><dd>{account.name}</dd>
                  <dt>Razón social</dt><dd>{account.legal_name || '—'}</dd>
                  <dt>Email</dt><dd>{account.email || '—'}</dd>
                  <dt>Email billing</dt><dd>{account.billing_email || '—'}</dd>
                  <dt>Teléfono</dt><dd>{account.phone || '—'}</dd>
                  <dt>CUIT/NIF</dt><dd>{account.tax_id || '—'}</dd>
                  <dt>Creado</dt><dd>{formatDate(account.created_at)}</dd>
                </dl>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><h3>Dirección y cuenta</h3></div>
              <div className="card-body">
                <dl className="dl" style={{ gridTemplateColumns: '130px 1fr' }}>
                  <dt>Dirección</dt><dd>{account.address || '—'}</dd>
                  <dt>Ciudad</dt><dd>{account.city || '—'}</dd>
                  <dt>Provincia</dt><dd>{account.state || '—'}</dd>
                  <dt>País</dt><dd>{account.country_id || '—'}</dd>
                  <dt>Owner</dt><dd>{account.owner_user_id ? <span className="code-chip">{account.owner_user_id.slice(0, 8)}</span> : '—'}</dd>
                  <dt>Actualizado</dt><dd>{formatDate(account.updated_at)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'tenants' ? (
        <div className="table-wrap">
          {tenants.length === 0 ? (
            <EmptyState icon="building" title="Sin tenants" desc="Este cliente todavía no tiene tiendas asociadas." />
          ) : (
            <table className="table">
              <thead><tr><th>Tenant</th><th>Status</th><th>Versión</th><th>Creado</th><th /></tr></thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant.id} onClick={() => navigate(`/tenants/${tenant.id}`)} {...rowActivation(() => navigate(`/tenants/${tenant.id}`))}>
                    <td><div className="cell-primary">{tenant.business_name}</div><div className="cell-secondary"><span className="code-chip">{tenant.slug}</span>{tenant.custom_domain ? ` · ${tenant.custom_domain}` : ''}</div></td>
                    <td><StatusBadge status={tenant.subscription_status} size="sm" /></td>
                    <td><span className="code-chip">{tenant.current_version || tenant.desired_version || 'n/a'}</span></td>
                    <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{formatDate(tenant.created_at)}</span></td>
                    <td style={{ textAlign: 'right' }}><Icon name="chevronRight" size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {tab === 'subscriptions' ? (
        <div className="col gap-4">
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi-card"><div className="kpi-label">Activas</div><div className="kpi-value">{subscriptionSummary.active}</div></div>
            <div className="kpi-card"><div className="kpi-label">Past due</div><div className="kpi-value">{subscriptionSummary.past_due}</div></div>
            <div className="kpi-card"><div className="kpi-label">Canceladas</div><div className="kpi-value">{subscriptionSummary.cancelled}</div></div>
            <div className="kpi-card"><div className="kpi-label">MRR</div><div className="kpi-value">{formatCurrency(mrr, 'ARS')}</div></div>
          </div>
          <div className="table-wrap">
            {subscriptionsLoading ? <TableSkeleton columns={6} rows={5} /> : subscriptions.length === 0 ? <EmptyState icon="refresh" title="Sin suscripciones" desc="No hay suscripciones asociadas a este cliente." /> : (
              <table className="table">
                <thead><tr><th>Plan</th><th>Status</th><th>Ciclo</th><th style={{ textAlign: 'right' }}>Precio</th><th>Período actual</th><th>Creada</th></tr></thead>
                <tbody>
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id}>
                      <td><div className="cell-primary">{subscription.plan_name || subscription.plan_id}</div><div className="cell-secondary"><span className="code-chip">{subscription.id.slice(0, 8)}</span></div></td>
                      <td><StatusBadge status={subscription.status} size="sm" /></td>
                      <td><span className="badge badge-outline badge-sm">{subscription.billing_cycle === 'monthly' ? 'Mensual' : 'Anual'}</span></td>
                      <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{formatCurrency(subscription.plan_price || 0, 'ARS')}</span></td>
                      <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{subscription.current_period_end ? formatDate(subscription.current_period_end) : '—'}</span></td>
                      <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{formatDate(subscription.created_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {tab === 'invoices' ? (
        <div className="col gap-4">
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi-card"><div className="kpi-label">Facturas</div><div className="kpi-value">{invoices.length}</div></div>
            <div className="kpi-card"><div className="kpi-label">Cobrado</div><div className="kpi-value">{formatCurrency(paidTotal, 'ARS')}</div></div>
            <div className="kpi-card"><div className="kpi-label">Pendiente</div><div className="kpi-value">{formatCurrency(openTotal, 'ARS')}</div></div>
          </div>
          <div className="table-wrap">
            {invoicesLoading ? <TableSkeleton columns={6} rows={5} /> : invoices.length === 0 ? <EmptyState icon="file" title="Sin facturas" desc="No hay facturas asociadas a este cliente." /> : (
              <table className="table">
                <thead><tr><th>Número</th><th>Tenant</th><th>Status</th><th style={{ textAlign: 'right' }}>Total</th><th>Emitida</th><th /></tr></thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} onClick={() => navigate(`/invoices/${invoice.id}`)} {...rowActivation(() => navigate(`/invoices/${invoice.id}`))}>
                      <td><span className="code-chip">{invoice.number || invoice.id.slice(0, 8)}</span></td>
                      <td><div className="cell-primary">{invoice.tenant_business_name || '—'}</div><div className="cell-secondary">{invoice.tenant_slug || ''}</div></td>
                      <td><StatusBadge status={invoice.status} size="sm" /></td>
                      <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{formatCurrency(invoice.total || 0, invoice.currency || 'ARS')}</span></td>
                      <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{invoice.issued_at ? formatDate(invoice.issued_at) : '—'}</span></td>
                      <td style={{ textAlign: 'right' }}><Icon name="chevronRight" size={14} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {tab === 'activity' ? (
        <div className="card">
          <div className="card-header"><h3>Actividad</h3></div>
          <div className="card-body">
            {auditLoading ? <Skeleton height={220} /> : auditLogs.length === 0 ? (
              <div className="muted" style={{ fontSize: 13, padding: 8 }}>Sin actividad registrada para este cliente.</div>
            ) : (
              <div className="col gap-2">
                {auditLogs.map((log) => (
                  <div key={log.id} className="row gap-2" style={{ fontSize: 12.5, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <Icon name="activity" size={12} />
                    <span style={{ flex: 1 }}>{log.action || 'acción'}{log.entity_type ? ` · ${log.entity_type}` : ''}</span>
                    <span className="muted" title={formatDateTime(log.created_at)} style={{ fontSize: 11 }}>{relTime(log.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}
